import { FunctionComponent, memo, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import sha1 from 'sha1';
import { UserContext } from '../../../../context';
import {
  FileStatus,
  MediaBlockUploadPreview,
  VideoBlockData,
  VideoPlayer,
  useAnalytics,
} from '../../../../shared';

import classes from './Video.module.scss';

interface VideoProps extends VideoBlockData {
  storyId?: number;
  cardId?: number;
  blockId?: string;
}

export const Video: FunctionComponent<VideoProps> = memo(({ items, storyId, cardId, blockId }) => {
  const { t } = useTranslation();

  const { logItemPlayed, logPostVideoPlayed } = useAnalytics();

  const { id: userId, channelId } = useContext(UserContext).userInfo.userData;

  const onLogItemPlayed = useCallback(
    (duration: number) => {
      const logArgs = {
        channel_id: channelId,
        story_id: storyId ?? 0,
        item_id: cardId ?? 0,
        duration,
        time_stamp: new Date().toISOString(),
      };

      if (blockId) {
        logPostVideoPlayed({ ...logArgs, block_id: blockId });
        return;
      }

      logItemPlayed({ ...logArgs, user_Id: sha1(`${userId}`) });
    },
    [blockId, cardId, channelId, logItemPlayed, logPostVideoPlayed, storyId, userId]
  );

  if (!items.length) {
    return null;
  }

  const { video, caption, copyright, previewUrl } = items[0];

  if (!video) {
    return null;
  }

  const { url, status } = video;

  if (status === FileStatus.PROCESSING) {
    return <Skeleton height={'5rem'} />;
  }

  if (!url) {
    return (
      <MediaBlockUploadPreview iconId={'video'} label={t('richTextEditor.video-placeholder')} />
    );
  }

  return (
    <div className={classes['video']}>
      <VideoPlayer
        url={url}
        thumb={previewUrl}
        caption={caption}
        copyright={copyright}
        captionClassName={classes['video__caption']}
        onLogItemPlayed={onLogItemPlayed}
      />
    </div>
  );
});
